import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { ChevronDown } from "lucide-react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { canvasToolbarButtonVariants } from "./canvasToolbarStyles";

interface CanvasToolbarButtonProps extends ComponentPropsWithoutRef<"button"> {
  /** Tooltip label; omit to hide tooltip. */
  label?: ReactNode;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  active?: boolean;
  destructive?: boolean;
  accent?: boolean;
  dropdown?: boolean;
  /** Optional mono text label inside the button (e.g. zoom %). */
  monoLabel?: ReactNode;
}

export function CanvasToolbarButton({
  label,
  tooltipSide = "top",
  active = false,
  destructive = false,
  accent = false,
  dropdown = false,
  monoLabel,
  className,
  children,
  disabled,
  ...rest
}: CanvasToolbarButtonProps) {
  const button = (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active ? true : undefined}
      // String tooltip labels double as the accessible name (design-system
      // toolbar contract); an explicit aria-label in `rest` still wins.
      aria-label={typeof label === "string" ? label : undefined}
      className={cn(
        canvasToolbarButtonVariants({
          active,
          destructive,
          accent,
          hasDropdown: dropdown,
        }),
        className,
      )}
      {...rest}
    >
      <span className="inline-flex shrink-0 items-center justify-center">
        {children}
      </span>
      {monoLabel != null && (
        <span className="font-mono text-xs leading-none">{monoLabel}</span>
      )}
      {dropdown && (
        <span className="inline-flex shrink-0 items-center text-n-50 group-hover:text-n-70">
          <ChevronDown className="size-2.5" strokeWidth={2.5} />
        </span>
      )}
    </button>
  );

  if (!label) {
    return button;
  }

  return (
    // 400ms show delay + 12px offset — the design-system toolbar tooltip
    // cadence and clearance.
    <Tooltip delayDuration={400}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side={tooltipSide} sideOffset={12}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
